// The Schedule of Work's area sections: per area its surfaces, notes,
// exclusions, defects, access/safety/preliminaries and photos.
import { categoryHeading, photoCaption, type ScheduleArea, type ScheduleOptions, type SurfaceRow } from './model';
import { fit } from './pdf-cover';
import { BRAND, CONTENT_WIDTH, PAGE, PdfPage, table, type Column } from './pdf-kit';
import type { LoadedPhoto } from './photos';
import { formatQuantity } from '../../components/surface-format';

type TableRow = Parameters<typeof table>[2][number];

const PHOTO_GRID_HEIGHT = 70;
const PHOTO_GAP = 4;
const CAPTION_SIZE = 7.5;

const one = (n: number | undefined) => (n === undefined ? '' : formatQuantity(n));
const quantity = (r: SurfaceRow) => (r.quantity === undefined ? '' : `${formatQuantity(r.quantity)} ${r.unit ?? ''}`.trim());

function surfaceTable(page: PdfPage, area: ScheduleArea, options: ScheduleOptions) {
  const hours = options.hoursSource !== 'none' && options.showHours;
  const litres = options.hoursSource !== 'none' && options.showLitres;
  const numbers: { label: string; cell: (r: SurfaceRow) => string; total: string }[] = [
    ...(options.showM2 ? [{ label: 'Quantity', cell: quantity, total: `${formatQuantity(area.totals.m2)} m²` }] : []),
    ...(hours ? [{ label: area.hoursLabel, cell: (r: SurfaceRow) => one(r.hours), total: one(area.totals.hours) }] : []),
    ...(litres ? [{ label: 'Litres', cell: (r: SurfaceRow) => one(r.litres), total: one(area.totals.litres) }] : []),
  ];
  const width = 30;
  const columns: Column[] = [{ label: 'Surface', width: CONTENT_WIDTH - numbers.length * width }, ...numbers.map((n) => ({ label: n.label, width, align: 'right' as const }))];
  const blank = numbers.map(() => '');
  const rows = area.rows.flatMap((r): TableRow[] => {
    if (r.kind === 'group') return [{ cells: [r.label, ...blank], bold: true, shade: true }];
    const row = { cells: [r.kind === 'subtotal' ? `   ${r.label}` : r.label, ...numbers.map((n) => n.cell(r))], bold: r.kind === 'subtotal' };
    // A surface with several paint systems lists them on the line under it (#412).
    return r.systems ? [row, { cells: [`   ${r.systems}`, ...blank], bold: false }] : [row];
  });
  if (numbers.length) rows.push({ cells: [hours ? area.totalLabel : 'Area total', ...numbers.map((n) => n.total)], bold: true, shade: false });
  table(page, columns, rows);
  if (area.hoursNote && hours) page.paragraph(area.hoursNote, { size: 7.5, colour: BRAND.mid, gapAfter: 3 });
}

function accessSection(page: PdfPage, area: ScheduleArea) {
  if (!area.access.length && !area.accessNotes) return;
  page.ensure(16);
  page.paragraph('ACCESS, SAFETY AND PRELIMINARIES', { size: 8.5, style: 'bold', colour: BRAND.orange, gapAfter: 1.5 });
  for (const block of area.access) {
    page.paragraph(categoryHeading(block.category), { size: 9, style: 'bold', gapAfter: 0.5 });
    page.paragraph(block.leadIn, { size: 8.5, colour: BRAND.mid, gapAfter: 0.5 });
    for (const item of block.items) page.bullet(item);
    page.y += 2;
  }
  if (area.accessNotes) page.paragraph(`Access notes: ${area.accessNotes}`, { size: 9, gapAfter: 3 });
}

/** Two photos a row, each captioned with its file name and direction ("Walls 01 · facing NE"). */
function photoGrid(page: PdfPage, area: ScheduleArea, photos: ReadonlyMap<string, LoadedPhoto>) {
  const shown = area.photos.filter((p) => photos.has(p.id));
  if (!shown.length) return;
  const cellWidth = (CONTENT_WIDTH - PHOTO_GAP) / 2;
  page.ensure(PHOTO_GRID_HEIGHT + 12);
  page.paragraph(`Photos (${shown.length})`, { size: 8.5, style: 'bold', colour: BRAND.orange, gapAfter: 1.5 });
  for (let i = 0; i < shown.length; i += 2) {
    page.ensure(PHOTO_GRID_HEIGHT + 6);
    const top = page.y;
    shown.slice(i, i + 2).forEach((ref, j) => {
      const photo = photos.get(ref.id) as LoadedPhoto;
      const size = fit(photo, cellWidth, PHOTO_GRID_HEIGHT);
      const x = PAGE.margin + j * (cellWidth + PHOTO_GAP);
      page.doc.addImage(photo.dataUrl, 'JPEG', x, top, size.width, size.height);
      page.font(CAPTION_SIZE, 'normal', BRAND.mid);
      page.doc.text(photoCaption(ref), x, top + size.height + 3.5);
    });
    page.y = top + PHOTO_GRID_HEIGHT + 6;
  }
}

function areaSection(page: PdfPage, area: ScheduleArea, options: ScheduleOptions, photos: ReadonlyMap<string, LoadedPhoto>) {
  page.ensure(45);
  page.bar(`${area.side.toUpperCase()} · ${area.name}`, area.side === 'Internal' ? BRAND.purple : BRAND.orange);
  if (options.showSurfaces && area.rows.length) surfaceTable(page, area, options);
  if (area.scopeNotes) page.noteBox('Scope notes', area.scopeNotes, BRAND.orange);
  if (area.exclusions) page.noteBox('Excluded from scope', area.exclusions, BRAND.purple);
  if (area.defects) page.noteBox('Defects / remediation', area.defects, BRAND.charcoal);
  accessSection(page, area);
  photoGrid(page, area, photos);
  page.y += 4;
}

/** Every area, internal first; each side starts on a new page. */
export function drawAreas(page: PdfPage, areas: ScheduleArea[], options: ScheduleOptions, photos: ReadonlyMap<string, LoadedPhoto>) {
  let side: string | null = null;
  for (const area of areas) {
    if (area.side !== side) {
      page.newPage();
      side = area.side;
    }
    areaSection(page, area, options, photos);
  }
}
